import React, { useMemo } from 'react';
import { isEmpty, isArray, isUndefined } from 'lodash';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { DataGridPro, GridToolbarColumnsButton } from '@mui/x-data-grid-pro';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import useGridLocalization from 'components/SearchResultsMUI/useGridLocalization';
import { DATA_GRID_DEFAULT_SORTING, DATA_GRID_ROW_HEIGHT } from 'constants/constants';
import { columnsForDocumentGrid } from 'utils/grid/ColumnsForDocumentGrid';
import { IDocumentGrid } from './_types';

export const DocumentGrid: React.FC<IDocumentGrid> = ({ gridKey }) => {
  const { t } = useTranslation('ClinicalPortal');
  const { compactMode } = useAppInfo();
  const gridLocalization = useGridLocalization();

  const rows: any[] = [
    {
      id: 1,
      typeOfDocumentName: t('typeOfDocument'),
      createdDateTime: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      author: t('author'),
      referenceID: t('referenceID'),
      externalId: t('externalId'),
      externalWorkplaceCode: t('externalWorkplaceCode'),
      externalWorkplaceName: t('externalWorkplaceName'),
      description: t('description'),
    },
  ];
  const {
    gridSortModel,
    columnVisibilityModel,
    injectColumnWidthsIntoColumns,
    reorderColumnsByGridSettings,
    changeWidthColumn,
    updateColumnOrder,
    setColumnVisibilityModel,
    sortByColumn,
  } = useMuiGrid(gridKey);

  const defaultHiddenColumnNames = {};

  const columnVisibilityModelWithDefaultHidden = {
    ...(isUndefined(columnVisibilityModel) ? defaultHiddenColumnNames : columnVisibilityModel),
  };

  const columns = useMemo(
    () =>
      reorderColumnsByGridSettings(
        injectColumnWidthsIntoColumns(columnsForDocumentGrid({ t, emptyAction: true })),
      ),
    [injectColumnWidthsIntoColumns, reorderColumnsByGridSettings, t],
  );

  const QuickSearchToolbar = (props: any) => {
    return (
      <Box style={{ padding: '5px', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <GridToolbarColumnsButton />
        </Box>
      </Box>
    );
  };

  return (
    <DataGridPro
      sortingOrder={DATA_GRID_DEFAULT_SORTING}
      headerFilters={false}
      rowHeight={DATA_GRID_ROW_HEIGHT}
      density={compactMode ? 'compact' : 'standard'}
      rows={rows}
      columns={columns}
      localeText={gridLocalization}
      slots={{ toolbar: QuickSearchToolbar }}
      columnVisibilityModel={columnVisibilityModelWithDefaultHidden}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      onColumnWidthChange={(changedColumn: any) => changeWidthColumn(columns, changedColumn)}
      onColumnOrderChange={(params: any) => updateColumnOrder(columns, params, 0)}
      sortModel={
        isArray(gridSortModel) && !isEmpty(gridSortModel)
          ? gridSortModel
          : [{ field: 'createdDateTime', sort: 'desc' }]
      }
      onSortModelChange={sortByColumn}
      hideFooter={true}
      disableColumnFilter={true}
      disableColumnPinning={true}
    />
  );
};
